import React, { useRef, useState } from 'react'
import { useAuth } from '../lib/AuthContext'
import { reviewApi } from '../lib/reviewApi'
import { useReviewRequest } from '../lib/useReviewRequest'
import './ReviewRequests.css'

export default function ManualReviewRequest({ workshopId, customerId }) {
  const { user, profile } = useAuth()
  if (!user?.id || profile?.id !== user.id || profile?.role !== 'owner' || profile?.workshop_id !== workshopId) return null
  return <ManualReviewForm key={`${user.id}:${workshopId}:${customerId}`} workshopId={workshopId} customerId={customerId} />
}

function ManualReviewForm({ workshopId, customerId }) {
  const ticket = useReviewRequest()
  const working = useRef(false)
  const [open, setOpen] = useState(false)
  const [jobs, setJobs] = useState([])
  const [next, setNext] = useState(null)
  const [selected, setSelected] = useState([])
  const [preview, setPreview] = useState(null)
  const [requestId, setRequestId] = useState(null)
  const [confirmed, setConfirmed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  async function run(action) {
    if (working.current) return
    working.current = true
    const current = ticket()
    setBusy(true); setError('')
    try { await action(current) }
    catch (e) { if (current()) setError(e.message) }
    finally { if (current()) { working.current = false; setBusy(false) } }
  }
  function load(before = null) {
    setOpen(true)
    if (!before) { setSelected([]); setPreview(null); setConfirmed(false); setRequestId(null) }
    run(async current => {
      const data = await reviewApi(workshopId, 'manual_jobs', { customer_id: customerId, before })
      if (!current()) return
      setJobs(old => before ? [...old, ...data.jobs.filter(j => !old.some(x => x.id === j.id))] : data.jobs)
      setNext(data.next)
    })
  }
  function choose(id) {
    setSelected(old => old.includes(id) ? old.filter(x => x !== id) : [...old, id])
    setPreview(null); setConfirmed(false); setRequestId(null); setError('')
  }
  function showPreview() {
    setPreview(null); setConfirmed(false)
    run(async current => {
      const data = await reviewApi(workshopId, 'manual_preview', { customer_id: customerId, job_ids: selected })
      if (!current()) return
      setPreview(data); setRequestId(crypto.randomUUID())
    })
  }
  function confirm() {
    if (!preview || !confirmed || preview.blockers.length) return
    run(async current => {
      const result = await reviewApi(workshopId, 'manual_confirm', { customer_id: customerId, job_ids: selected,
        confirmation_hash: preview.confirmation_hash, request_id: requestId, confirmed: true })
      if (current()) { setDone(result.state || 'pending'); setPreview(null); setConfirmed(false) }
    })
  }
  return <section className="review-manual" aria-label="Manual review request">
    <h3>Review request for older work</h3>
    <p>Choose paid jobs for this customer and preview one optional invitation. This does not give permission or enable automatic invitations.</p>
    {!open && <button type="button" className="btn btn-secondary" onClick={() => load()}>Send review request for older work</button>}
    {error && <p role="alert" className="review-error">{error}</p>}
    {open && !done && <>
      <p>Uses the saved customer email. Save any customer changes first. Select up to ten jobs; existing pending work is included in the preview.</p>
      <fieldset disabled={busy}><legend>Choose this customer’s jobs</legend>
        {jobs.map(job => <label className="review-choice" key={job.id}><input type="checkbox" checked={selected.includes(job.id)}
          disabled={!!job.blocker || (!selected.includes(job.id) && selected.length >= 10)} onChange={() => choose(job.id)} />
          <span><strong>{job.job_number || job.id}</strong> · {job.module === 'general' ? 'General' : 'Suspension'}<br />
            {job.paid_at ? `Paid ${new Date(job.paid_at).toLocaleDateString('en-GB')}` : 'Full payment not verified'}
            {job.blocker && <span className="review-job-blocker">{job.blocker}</span>}</span></label>)}
      </fieldset>
      {!jobs.length && !busy && <p>No active Rebound job records found. Imported historical notes do not qualify.</p>}
      {busy && <p role="status">Checking review request…</p>}
      <div className="review-actions">
        {next && <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => load(next)}>Load older jobs</button>}
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => load()}>Refresh jobs</button>
        <button type="button" className="btn btn-primary" disabled={busy || !selected.length} onClick={showPreview}>Preview review request</button>
      </div>
      {preview && <>
        <div className="review-preview" aria-label="Manual review email preview">
          <h3>Confirm one review invitation</h3>
          <dl><dt>To</dt><dd>{preview.recipient || 'Saved email required'}</dd><dt>Workshop</dt><dd>{preview.workshop_name}</dd>
            <dt>From</dt><dd>{preview.email?.from || preview.from_email || 'Verified sender required'}</dd><dt>Reply to</dt><dd>{preview.reply_to || 'Setup required'}</dd>
            <dt>Google destination</dt><dd>{preview.review_url || 'Setup required'}</dd><dt>Subject</dt><dd>{preview.email?.subject || 'Email identity needs setup'}</dd></dl>
          <p>{preview.jobs.length} job{preview.jobs.length === 1 ? '' : 's'} in one invitation{preview.pending_id ? ' — combined with the existing pending invitation' : ''}:</p>
          <ul>{preview.jobs.map(j => <li key={j.id}>{j.job_number || j.id} · {j.module}</li>)}</ul>
          {preview.email && <div className="review-copy">{preview.email.text}</div>}
          <p className="text-muted fs12">The email uses this wording and identity. A secure stop-invitations link is added when it is sent. Previewing changes nothing.</p>
        </div>
        {!!preview.blockers.length && <div role="alert" className="review-notice"><strong>Not ready to send</strong><ul>{preview.blockers.map((reason, n) => <li key={n}>{reason}</li>)}</ul>
          <p>Resolve these separately, then preview again. No permission or sending setting has been changed.</p></div>}
        <label className="review-choice"><input type="checkbox" checked={confirmed} disabled={busy || !!preview.blockers.length || !preview.email}
          onChange={e => setConfirmed(e.target.checked)} /> I have checked the recipient, workshop and jobs and want to queue this one review invitation.</label>
        <button type="button" className="btn btn-primary" disabled={busy || !confirmed || !!preview.blockers.length || !preview.email} onClick={confirm}>Confirm and queue review request</button>
        <p className="text-muted fs12">Sending requires the approved delivery service. Confirmation does not mean an email has been delivered. Failed or uncertain requests cannot be resent here.</p>
      </>}
    </>}
    {done && <p role="status">{done === 'pending'
      ? 'One review invitation is queued for this customer. Check invitation history for the outcome; no further request is needed.'
      : 'This review request is already recorded. Check invitation history for its current status; do not send it again.'}</p>}
  </section>
}
